import { Match } from "model/Match/Match";
import { MatchTeams } from "model/Match/MatchTeams";
import { ReverseCupModeSet } from "model/Match/Set/CupMode/ReverseCupModeSet";
import { TrackRound } from "model/Match/Track/TrackRound";
import {
  getSeasonRosterPointsOverride,
  SeasonPointsOverrides,
} from "model/Tournament/Segment/Season/Season";
import { statRoundsBestCombinedTime } from "stats/RoundStats";
import { nonUndefined, onlyUnique } from "Utility";

const POSITION_POINTS = [9, 6, 3, 1];

export type AMSeasonRecord = {
  rosterId: string;
  matchPoints: ({ position: number; fastestTime: number } | undefined)[];
  points: number | undefined;
  position: number | undefined;
  positions: number[];
};

export type AMSeasonRecordWithDisplay = {
  record: AMSeasonRecord;
  orgId: string | undefined;
  displayName: string | undefined;
};

const createAMSeasonRecord = (
  rosterId: string,
  numberOfPlaydays: number
): AMSeasonRecord => {
  return {
    rosterId,
    matchPoints: Array(numberOfPlaydays).fill(undefined),
    points: undefined,
    position: undefined,
    positions: [0, 0, 0, 0],
  };
};

export const calculateAMSeasonRecords = (
  seasonPointsOverrides: SeasonPointsOverrides | undefined,
  rosterIds: string[],
  matchesByPlayday: Match[][],
  numberOfPlaydays: number
) => {
  const records = new Map<string, AMSeasonRecord>();
  rosterIds.forEach((id) => {
    records.set(id, createAMSeasonRecord(id, numberOfPlaydays));
  });

  for (let playdayI = 0; playdayI < matchesByPlayday.length; playdayI++) {
    const matches = matchesByPlayday[playdayI];
    for (let matchI = 0; matchI < matches.length; matchI++) {
      const match = matches[matchI];
      if (!match.finalPositions) {
        continue;
      }

      const set = match.sets[0] as ReverseCupModeSet;

      // Fastest Time Points
      const matchTrackIds = nonUndefined(
        set.tracks.map((t) => t.trackId)
      ).filter(onlyUnique);

      const fastestRosterIds = matchTrackIds.map((id) =>
        getFastestTrackPointRosterId(set, id, match.teams)
      );

      for (let rosterI = 0; rosterI < rosterIds.length; rosterI++) {
        const rosterId = rosterIds[rosterI];
        const rosterRecord = records.get(rosterId);
        const finalPos = match.finalPositions[rosterId];
        if (!rosterRecord || !finalPos) {
          continue;
        }
        const overrideScore = !!seasonPointsOverrides
          ? getSeasonRosterPointsOverride(
              seasonPointsOverrides,
              match.id,
              rosterId
            )
          : undefined;

        const positionScore =
          (overrideScore?.result === "Qual" ? 9999 : overrideScore?.result) ??
          POSITION_POINTS.at(finalPos - 1) ??
          0;
        const bonusScore =
          overrideScore?.bonus ??
          fastestRosterIds.filter((id) => id === rosterId).length;

        rosterRecord.matchPoints[playdayI] = {
          position: positionScore,
          fastestTime: bonusScore,
        };

        if (!rosterRecord.points) {
          rosterRecord.points = 0;
        }

        rosterRecord.points += positionScore + bonusScore;
      }
    }
  }

  const sortedRecords = Array.from(records.values()).sort(compareRecords);
  if (sortedRecords.length === 0 || sortedRecords[0].points === undefined) {
    return sortedRecords;
  }

  sortedRecords[0].position = 1;
  for (let i = 1; i < sortedRecords.length; i++) {
    if (sortedRecords[i].points === undefined) {
      break;
    }

    if (compareRecords(sortedRecords[i - 1], sortedRecords[i]) === 0) {
      sortedRecords[i].position = sortedRecords[i - 1].position;
    } else {
      sortedRecords[i].position = i + 1;
    }
  }

  return sortedRecords;
};

const compareRecords = (a: AMSeasonRecord, b: AMSeasonRecord): number => {
  if (a.points !== b.points) {
    if (a.points === undefined) {
      return 1;
    }

    if (b.points === undefined) {
      return -1;
    }

    return b.points - a.points;
  }

  return 0;
};

const getFastestTrackPointRosterId = (
  set: ReverseCupModeSet,
  trackId: string,
  teams: MatchTeams
) => {
  const trackRounds = set.tracks
    .filter((t) => t.trackId === trackId)
    .flatMap((t) => t.rounds as TrackRound[]);

  return statRoundsBestCombinedTime(trackRounds, teams)?.rosterId;
};
